import type { V2BreadcrumbSegment } from '@sakhtar/design-system/react';
import { CRM_TABS } from './modules/CrmModuleLayout.js';
import { FINANCE_TABS } from './modules/FinanceModuleLayout.js';
import { INVENTORY_TABS } from './modules/InventoryModuleLayout.js';
import { PROCUREMENT_TABS } from './modules/ProcurementModuleLayout.js';
import { PROJECTS_TABS } from './modules/ProjectsModuleLayout.js';
import { MASTER_DATA_TABS } from './modules/MasterDataModuleLayout.js';
import { computeActiveTab, type TabLike } from './modules/tabMatch.js';
import type { NavEntry } from './navConfig.js';

/** Every module nav-entry id that owns a persistent tab strip (`ModuleLayout`/`V1ModuleTabs`). */
const MODULE_TABS: Record<string, readonly TabLike[]> = {
  crm: CRM_TABS,
  finance: FINANCE_TABS,
  inventory: INVENTORY_TABS,
  procurement: PROCUREMENT_TABS,
  projects: PROJECTS_TABS,
  'master-data': MASTER_DATA_TABS,
};

/**
 * Clean Persian breadcrumb trail — "خانه / فروش و CRM / سرنخ‌ها" — replacing
 * the old two-segment `/t/{slug} / X` breadcrumb, which read as raw route
 * metadata rather than a page location. Root is always "خانه" (home), never
 * the tenant slug — the slug has its own place in the shell (topbar user
 * menu / tenant switcher), not in every breadcrumb. A bare "خانه" (the home
 * page itself) is a valid one-segment trail, not a special case, so it never
 * renders as "خانه / خانه".
 *
 * For a module with a tab strip (CRM, Finance, Inventory, Procurement,
 * Projects, Master Data) the trail adds the module label, then — unless
 * we're on the module's own overview tab — the active tab's label, reusing
 * the exact same prefix-matching `computeActiveTab` the tab strip itself
 * uses, so the breadcrumb leaf and the highlighted tab never disagree.
 */
export function computeBreadcrumb(
  pathname: string,
  base: string,
  activeEntry: NavEntry | undefined,
): readonly V2BreadcrumbSegment[] {
  const home: V2BreadcrumbSegment = { label: 'خانه' };
  if (!activeEntry || activeEntry.id === 'home') return [home];

  const tabs = MODULE_TABS[activeEntry.id];
  if (!tabs) return [home, { label: activeEntry.label }];

  const moduleBase = `${base}${activeEntry.hrefSuffix}`;
  const activeTab = computeActiveTab(pathname, moduleBase, tabs);
  if (activeTab.hrefSuffix === '') return [home, { label: activeEntry.label }];
  return [home, { label: activeEntry.label }, { label: activeTab.label }];
}
